import { vec2 } from 'gl-matrix';
import { PanZoom } from '../../view/PanZoom';
import { View } from '../../view/View';
import { PlaneView } from '../../view/views/PlaneView';
import { EventHandlerResult } from '../EventHandlerResult';
import { ModifierKeys } from '../ModifierKeys';
import { PanAction } from '../actions/PanAction';
import {
    MOUSE_BUTTON_LEFT,
    MOUSE_BUTTON_MIDDLE,
    MOUSE_BUTTONS_LEFT_MASK,
} from '../MouseButton';
import {
    PalmCursor,
    PalmCursorMode,
} from '../../data/common/customs/cursors/PalmCursor';
import { GlobalCoordinator } from './GlobalCoordinator';

/**
 * Configuration for global middle-button panning and alternative key combos.
 */
const PANNING_CONFIG = {
    /** Whether panning with the Space key + left mouse button
     *  + mouse drag is enabled. */
    isPanningWithSpaceBarEnabled: true,
    /** Whether panning with the Cmd/Meta key + left mouse button
     *  + mouse drag is enabled. */
    isPanningWithMetaKeyEnabled: true,
    /** Mouse button for panning (without modifier keys). */
    mouseButton: MOUSE_BUTTON_MIDDLE,
};

/**
 * Owns all global-pan state and logic: key+drag pan, middle-button
 * pan, and the palm cursor feedback.
 *
 * Consumed exclusively by {@link ToolManager} — every mouse/key handler
 * delegates to this coordinator first and skips further dispatch
 * when the coordinator returns `true` (event consumed).
 */
export class GlobalPanCoordinator extends GlobalCoordinator {
    /** The global {@link PanAction} (middle-button or
     *  left+middle when PanTool is active). */
    private globalPanAction: PanAction | undefined;

    /** Whether the Space key is currently held down. */
    private isSpaceHeld = false;

    /** Whether the Meta/Cmd key is currently held down. */
    private isMetaHeld = false;

    /** Whether the current pan was initiated by a key+drag
     *  combo. */
    private wasCurrentPanActivatedViaKey = false;

    /** Cursor instance for pan visual feedback. */
    private panCursor: PalmCursor | undefined;

    /** Cached cursor mode to avoid redundant setMode calls. */
    private panCursorMode: PalmCursorMode | undefined;

    /** Whether the current pan (not key-initiated) has received
     *  at least one mousemove. Used to defer the GRAB cursor
     *  until actual dragging begins. */
    private isPanDragging = false;

    // ───────── cursor helpers ─────────

    /**
     * Evaluate the current pan state and apply the correct
     * cursor (open palm, closed fist, or clear).
     */
    private updatePanCursor(): void {
        const isPanning =
            this.globalPanAction?.isActive || this.wasCurrentPanActivatedViaKey;
        const isPanKeyHeld = this.isSpaceHeld || this.isMetaHeld;

        if (isPanning) {
            // Direct pan (not key-initiated): defer GRAB
            // cursor until actual mouse movement (drag)
            // occurs.
            if (!this.wasCurrentPanActivatedViaKey && !this.isPanDragging) {
                this.resetPanCursor();
                return;
            }
            if (!this.panCursor) {
                this.panCursor = new PalmCursor(this.views ?? null);
            }
            if (this.panCursorMode !== PalmCursorMode.GRAB) {
                this.panCursor.setMode(PalmCursorMode.GRAB);
                this.panCursorMode = PalmCursorMode.GRAB;
            }
        } else if (isPanKeyHeld) {
            // Open palm cursor — shown for ALL tools
            // including Pan tool when Space/Meta is held.
            if (!this.panCursor) {
                this.panCursor = new PalmCursor(this.views ?? null);
            }
            if (this.panCursorMode !== PalmCursorMode.DEFAULT) {
                this.panCursor.setMode(PalmCursorMode.DEFAULT);
                this.panCursorMode = PalmCursorMode.DEFAULT;
            }
        } else if (this.panCursor) {
            this.panCursor.resetCursor();
            this.panCursor = undefined;
            this.panCursorMode = undefined;
        }
    }

    /**
     * Force-clear the pan cursor (used on mouse leave).
     */
    private resetPanCursor(): void {
        if (this.panCursor) {
            this.panCursor.resetCursor();
            this.panCursor = undefined;
            this.panCursorMode = undefined;
        }
    }

    // ───────── public API ─────────

    /** Whether a global pan is currently in progress. */
    public get isActive(): boolean {
        return (
            (this.globalPanAction?.isActive ?? false) ||
            this.wasCurrentPanActivatedViaKey
        );
    }

    /** Whether Space or Meta/Cmd key is currently held. */
    public get isPanKeyHeld(): boolean {
        return this.isSpaceHeld || this.isMetaHeld;
    }

    /**
     * Refresh the global pan action's start state after an
     * external view change (e.g. slice scroll) so the pan
     * doesn't snap back to the old view state.
     */
    public refreshPanAfterViewChange(): void {
        this.globalPanAction?.refreshStartState();
    }

    /**
     * Re-assert the pan cursor after a tool update that may
     * have overridden it during an active pan (e.g.
     * LineDrawTool sets crosshair). Covers middle-mouse pan
     * as well as key+drag pan.
     */
    public refreshPanCursor(): void {
        if (this.panCursor) {
            this.panCursor.setCustomCursor();
        }
    }

    /**
     * Re-assert the pan cursor after tools have processed their
     * events. Ensures the palm cursor takes visual priority over
     * tool cursors (e.g. LineDrawTool's crosshair) when a pan
     * key is held.
     *
     * Unlike {@link updatePanCursor}, this bypasses the cached
     * mode check and forcefully re-applies the cursor CSS,
     * because a tool's onMouseMove may have overwritten the
     * same CSS property since the last updatePanCursor call.
     */
    public refreshPanCursorIfKeyHeld(isStrokeInProgress: () => boolean): void {
        if (
            (this.isSpaceHeld || this.isMetaHeld) &&
            !isStrokeInProgress() &&
            this.panCursor
        ) {
            this.panCursor.setCustomCursor();
        }
    }

    /**
     * Called by ToolManager.setActiveTool() to configure the
     * pan action for the newly active tool. Creates the
     * PanAction with the right button config (left+middle for
     * PanTool, middle-only for others).
     */
    public initialize(
        panZoom: PanZoom,
        isPanTool: boolean,
        views: PlaneView[] | undefined
    ): void {
        const globalPanButtons = isPanTool
            ? [MOUSE_BUTTON_LEFT, PANNING_CONFIG.mouseButton]
            : PANNING_CONFIG.mouseButton;
        this.globalPanAction = new PanAction(panZoom, globalPanButtons);
        this.isPanDragging = false;
        this.views = views;
        this.updatePanCursor();
    }

    /**
     * Pan handling for mouse press. Checks key+drag intercept
     * first, then global pan (middle mouse button).
     * @returns true if pan consumed the event.
     */
    public onMousePress(
        button: number,
        modifierKeys: ModifierKeys,
        coordinates: vec2,
        view: View,
        isToolActionInProgress: () => boolean,
        handleEventResult: (result: EventHandlerResult) => boolean
    ): boolean {
        // key+drag pan (Space+drag or Cmd+drag):
        // intercept left button when a pan key is held
        if (
            button === MOUSE_BUTTON_LEFT &&
            !isToolActionInProgress() &&
            this.globalPanAction &&
            view instanceof PlaneView &&
            ((PANNING_CONFIG.isPanningWithSpaceBarEnabled &&
                this.isSpaceHeld) ||
                (PANNING_CONFIG.isPanningWithMetaKeyEnabled && this.isMetaHeld))
        ) {
            this.globalPanAction.forceStart(coordinates, view);
            this.wasCurrentPanActivatedViaKey = true;
            this.updatePanCursor();
            return true;
        }

        // try the global pan action first (middle mouse
        // button), but not while another tool action is in
        // progress
        if (this.globalPanAction && !isToolActionInProgress()) {
            if (
                handleEventResult(
                    this.globalPanAction.onMousePress(
                        button,
                        modifierKeys,
                        coordinates,
                        view
                    )
                )
            ) {
                this.isPanDragging = false;
                this.updatePanCursor();
                return true;
            }
        }

        return false;
    }

    /**
     * Pan handling for mouse move. Handles key-pan resume after
     * mouse re-entry, then active pan movement. Always updates
     * cursor.
     * @returns true if pan consumed the event.
     */
    public onMouseMove(
        coordinates: vec2,
        buttons: number,
        view: View,
        isStrokeInProgress: () => boolean,
        handleEventResult: (result: EventHandlerResult) => boolean
    ): boolean {
        // Resume key-pan after mouse re-entry: the pan action
        // was cancelled on mouse leave but the pan key is
        // still held.
        if (
            this.wasCurrentPanActivatedViaKey &&
            this.globalPanAction &&
            !this.globalPanAction.isActive &&
            view instanceof PlaneView
        ) {
            // If the mouse button was released while outside
            // the window, cancel the key-pan instead of
            // resuming.
            if ((buttons & MOUSE_BUTTONS_LEFT_MASK) === 0) {
                this.wasCurrentPanActivatedViaKey = false;
            } else {
                this.globalPanAction.forceStart(coordinates, view);
            }
        }

        // try the global pan action first (middle mouse
        // button), but guard against auto-restart while a
        // tool-level action is in progress (e.g. brush
        // drawing with its own _panAction)
        if (this.globalPanAction && !isStrokeInProgress()) {
            if (
                handleEventResult(
                    this.globalPanAction.onMouseMove(coordinates, buttons, view)
                )
            ) {
                if (!this.wasCurrentPanActivatedViaKey) {
                    this.isPanDragging = true;
                }
                this.updatePanCursor();
                return true;
            }
        }
        // Skip cursor update while a stroke is active to
        // prevent the palm cursor from appearing mid-draw.
        if (!isStrokeInProgress()) {
            this.updatePanCursor();
        }
        return false;
    }

    /**
     * Pan handling for mouse release. Checks key+drag release
     * first, then global pan release.
     * @returns true if pan consumed the event.
     */
    public onMouseRelease(
        button: number,
        coordinates: vec2,
        handleEventResult: (result: EventHandlerResult) => boolean
    ): boolean {
        // key+drag pan release: intercept left button release
        // when key-pan is active
        if (button === MOUSE_BUTTON_LEFT && this.wasCurrentPanActivatedViaKey) {
            this.globalPanAction?.cancel();
            this.wasCurrentPanActivatedViaKey = false;
            this.updatePanCursor();
            return true;
        }

        // try the global pan action first (middle mouse
        // button)
        if (this.globalPanAction) {
            if (
                handleEventResult(
                    this.globalPanAction.onMouseRelease(button, coordinates)
                )
            ) {
                this.isPanDragging = false;
                this.updatePanCursor();
                return true;
            }
        }

        return false;
    }

    /**
     * Cancel active pan and reset cursor. Called on mouse
     * leave.
     */
    public onMouseLeave(): void {
        this.globalPanAction?.cancel();
        // Only clear keyPanActive if no pan key is held
        // anymore. If a key is still held, keep the flag so
        // pan restarts when the cursor re-enters the window.
        if (!this.isSpaceHeld && !this.isMetaHeld) {
            this.wasCurrentPanActivatedViaKey = false;
        }
        this.isPanDragging = false;
        this.resetPanCursor();
    }

    /**
     * Sync Meta/Cmd key state from a mouse event.
     * Bridges the gap when Meta was pressed outside the browser
     * window (so no keydown fired) but MouseEvent.metaKey is
     * true.
     */
    public syncMetaKeyState(
        isMetaDown: boolean,
        isStrokeInProgress?: () => boolean
    ): void {
        if (isMetaDown === this.isMetaHeld) {
            return;
        }
        if (isMetaDown) {
            this.onPanKeyDown('Meta', isStrokeInProgress);
        } else {
            this.onPanKeyUp('Meta', isStrokeInProgress);
        }
    }

    /** Document-level keydown for Space/Meta. */
    public onPanKeyDown(
        key: string,
        isStrokeInProgress?: () => boolean
    ): boolean {
        if (key === ' ' && PANNING_CONFIG.isPanningWithSpaceBarEnabled) {
            this.isSpaceHeld = true;
            if (!(isStrokeInProgress?.() ?? false)) {
                this.updatePanCursor();
            }
            return true;
        }
        if (key === 'Meta' && PANNING_CONFIG.isPanningWithMetaKeyEnabled) {
            this.isMetaHeld = true;
            if (!(isStrokeInProgress?.() ?? false)) {
                this.updatePanCursor();
            }
        }
        return false;
    }

    /** Document-level keyup for Space/Meta. */
    public onPanKeyUp(
        key: string,
        _isStrokeInProgress?: () => boolean
    ): boolean {
        if (key === ' ') {
            this.isSpaceHeld = false;
            if (this.wasCurrentPanActivatedViaKey) {
                this.globalPanAction?.cancel();
                this.wasCurrentPanActivatedViaKey = false;
            }
            this.updatePanCursor();
            return true;
        }
        if (key === 'Meta') {
            this.isMetaHeld = false;
            if (this.wasCurrentPanActivatedViaKey) {
                this.globalPanAction?.cancel();
                this.wasCurrentPanActivatedViaKey = false;
            }
            this.updatePanCursor();
            return true;
        }
        return false;
    }
}
